import React, { useState, useEffect } from "react";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import { AiOutlineEdit, AiOutlineDelete, AiOutlineUserAdd, AiOutlineSearch, AiOutlineEye } from 'react-icons/ai';
import { FaUsers } from "react-icons/fa6";
import { useAuth } from "../../context/Auth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify"; 
import Swal from "sweetalert2"; 
import { api_url } from "../../data";

const Cells = () => {
    const navigate = useNavigate();
    const { requestWithAuth } = useAuth();
    const [churchGroups, setChurchGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [deletingMemberId, setDeletingMemberId] = useState(null); 
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);


    const getChurchGroups = async (searchValue, pageSize, page) => {
        try {
            setLoadingGroups(true);
            const response = await requestWithAuth(`/groups/church-groups/?search=${searchValue}&page_size=${pageSize}&page=${page}`, 'GET');
            const response_data = response?.data;
            setChurchGroups(response_data.results);
            setNextPage(response_data?.next);
            setPrevPage(response_data?.prev);
            setTotalPages(Math.ceil(response_data?.count / pageSize));
        } catch (error) {
            toast.error("Failed to load members. Please try again.");
        } finally {
            setLoadingGroups(false);
        }
    };

    useEffect(() => {
        getChurchGroups(debouncedSearch, pageSize, currentPage);
    }, [debouncedSearch, pageSize, currentPage]);

    useEffect(()=>{
        const timer = setTimeout(()=>{
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(timer);
    }, [search])

    const initializeDeleteGroup = (memberId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteMember(memberId);
            }
        });
    };

    const deleteMember = async (memberId) => {
        setDeletingMemberId(memberId); 
        try {
            await requestWithAuth(`/members/${memberId}/`, 'DELETE');
            toast.success("Member deleted successfully!");
          
            // getRegisteredMembers(search, pageSize, currentPage);
        } catch (error) {
            console.error("Failed to delete member:", error);
            toast.error("Failed to delete member. Please try again.");
        } finally {
            setDeletingMemberId(null); 
        }
    };

    // Pagination Handlers
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    
    const renderPageNumbers = () => {
        const pageNumbers = [];
        const totalNumbersToShow = 3; 

        let startPage = Math.max(1, currentPage - Math.floor(totalNumbersToShow / 2));
        let endPage = Math.min(totalPages, startPage + totalNumbersToShow - 1);

        
        if (endPage - startPage + 1 < totalNumbersToShow) {
            startPage = Math.max(1, endPage - totalNumbersToShow + 1);
        }

        if (startPage > 1) {
            pageNumbers.push(1);
            if (startPage > 2) {
                pageNumbers.push("...");
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pageNumbers.push("...");
            }
            pageNumbers.push(totalPages);
        }

        return pageNumbers.map((number, index) => (
            <button
                key={index}
                onClick={() => number !== "..." && handlePageClick(number)}
                disabled={number === "..."}
                className={`
                    min-w-[40px] h-10 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors
                    ${number === currentPage
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : number === "..."
                            ? "bg-white text-gray-400 border-gray-200 cursor-default"
                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                    }
                `}
            >
                {number}
            </button>
        ));
    };

    // Helper function for role badge colors
    const getRoleBadgeColor = (role) => {
        switch (role?.toLowerCase()) {
            case 'elder':
                return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'deacon':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'member':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    // Helper function to get initials for avatar
    const getInitials = (groupName) => {
        const names = groupName.split(' ');
        return `${names[0]?.[0] || ''}${names[1]?.[0] || ''}`.toUpperCase();
    };

    const handleToggleStatus = async (user) => {
        if(user?.role === "Admin"){
            return toast.error("You cannot toggle the status of the admin user");
        }
        const newStatus = user?.status == "Active" ? "Inactive":"Active";
        try {
            
            const response = await requestWithAuth(
                `/members/${user.id}/`,
                "PUT",
                { membership_status: newStatus },
                { "Content-Type": "application/json" },
            );

            setChurchGroups(prevUsers =>
                prevUsers.map(u => (u.id === user.id ? { ...u, status: newStatus } : u))
            );
            toast.success(`Member ${user.full_name} has been set to ${newStatus}`);
        } catch (error) {
            
            toast.error("Failed to update user status");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50/50 p-6">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Group Management</h1>
                    <p className="text-gray-600 mt-1">Manage church groups and their information</p>
                </div>
                <button
                    onClick={() => navigate('/group-management/new')}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    <AiOutlineUserAdd className="text-lg" />
                    Add New Group
                </button>
            </div>

            

            {/* Cells Table Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Card Header with Search */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <h2 className="text-lg font-semibold text-gray-900">Church Groups</h2>
                        <div className="relative">
                            <AiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search groups..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all w-full sm:w-64"
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    {loadingGroups ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="flex flex-col items-center gap-3">
                                <LoadingSpinner />
                                <p className="text-gray-600 text-sm">Loading groups...</p>
                            </div>
                        </div>
                    ) : churchGroups.length > 0 ? (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Group Name</th>
                                    <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Group Category</th>
                                    <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Number of Members</th>
                                    <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {churchGroups.map((group) => (
                                    <tr key={group.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="py-4 px-6">
                                            <div className="flex items-center gap-3">
                                                {
                                                    group.group_logo ? (
                                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                                            <img src={`${api_url}${group?.group_logo}`} alt="" className="object-cover h-full w-full rounded-full" />
                                                        </div>
                                                    ):(
                                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                                            {getInitials(group.group_name)}
                                                        </div>
                                                    )
                                                }
                                                
                                                <div className="min-w-0">
                                                    <p className="font-medium text-gray-900 truncate">
                                                        {group.group_name}
                                                    </p>
                                                    
                                                </div>
                                            </div>
                                        </td>
                                       
                                        <td className="py-4 px-6">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(group.group_category)}`}>
                                                {group.group_category}
                                            </span>
                                        </td>
                                        
                                        <td className="py-4 px-6 hidden md:table-cell">
                                            <p className="font-medium text-gray-900 truncate">
                                                {group.members_in_group}
                                            </p>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => navigate(`/group-management/${group.id}/`)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="View Profile"
                                                >
                                                    <AiOutlineEye className="text-lg" />
                                                </button>
                                                 
                                                <button
                                                    onClick={() => initializeDeleteGroup(group.id)}
                                                    disabled={deletingMemberId === group.id} 
                                                    className={`p-2 rounded-lg transition-colors ${deletingMemberId === group.id ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}
                                                    title="Delete Group"
                                                >
                                                    {deletingMemberId === group.id ? (
                                                        <LoadingSpinner size="sm" /> // Small spinner for the button
                                                    ) : (
                                                        <AiOutlineDelete className="text-lg" />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <FaUsers className="text-gray-400 w-12 h-12" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {search ? 'No gropus found' : 'No groups yet'}
                            </h3>
                            <p className="text-gray-500 text-center mb-6 max-w-md">
                                {search
                                    ? `No members match "${search}". Try adjusting your search.`
                                    : 'Get started by adding your first group to the system.'
                                }
                            </p>
                            {!search && (
                                <button
                                    onClick={() => navigate('/group-management/new')}
                                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    <AiOutlineUserAdd className="text-lg" />
                                    Add First Group
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {churchGroups.length > 0 && (
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/30">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <select onChange={e=>setPageSize(e.target.value)} name="" id="" className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
                                    <option value="5">5</option>
                                    <option value="10" selected>10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <p className="text-sm text-gray-700">
                                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                                    <span className="font-medium">{totalPages}</span>
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                                        ${currentPage === 1
                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    Previous
                                </button>

                                <div className="flex items-center gap-1">
                                    {renderPageNumbers()}
                                </div>

                                <button
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                                        ${currentPage === totalPages
                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Cells;